/* API client.
 *
 * Sessions are httponly cookies, so every request sends credentials and no
 * token is ever handled in JavaScript. A 401 means the session is gone; the
 * caller redirects rather than retrying, because retrying a dead session just
 * produces a second 401.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }

  get isUnauthenticated() {
    return this.status === 401;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      /* a non-JSON error body is still an error */
    }
    throw new ApiError(response.status, detail);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  // `del`, not `delete` — `delete` is a reserved word and cannot be a bare
  // method name on an object literal used as `api.delete(...)` in all TS
  // configurations. The backend's DELETE endpoints answer 204, which
  // `request` already maps to undefined.
  del: <T = void>(path: string) => request<T>(path, { method: "DELETE" }),
};

/* --- Types --------------------------------------------------------------- */

export type PowerState = "running" | "stopped" | "paused" | "suspended" | "unknown";

export interface Me {
  id: string;
  email: string;
  full_name: string;
  is_staff: boolean;
  totp_enabled: boolean;
  active_org_id: string | null;
  organizations: { id: string; name: string; slug: string; role: string }[];
  permissions: string[];
  features: Record<string, boolean>;
}

export interface DiscoveredResource {
  id: string;
  cluster_id: string;
  cluster_name: string;
  kind: string;
  external_id: string;
  name: string;
  node_name: string;
  attributes: {
    power_state?: PowerState;
    cpu_cores?: number | null;
    memory_total_bytes?: number | null;
    disk_total_bytes?: number | null;
    uptime_seconds?: number | null;
    tags?: string[];
  };
  classification: "unclassified" | "customer" | "internal";
  classification_note: string;
  first_seen_at: string;
  last_seen_at: string;
  missing_since: string | null;
  service_id: string | null;
  org_id: string | null;
  org_name: string | null;
}

export interface DiscoveryStats {
  total: number;
  needs_attention: number;
  claimed: number;
  internal: number;
  missing: number;
}

export interface ConsoleSession {
  session_id: string;
  ttl_seconds: number;
  kind: "vnc" | "serial";
  websocket_path: string;
  service_id: string;
}

/* --- DNS ----------------------------------------------------------------- */

export type ZoneVerificationState = "pending" | "verified" | "failed";

export interface DnsZone {
  id: string;
  name: string;
  verification_state: ZoneVerificationState;
  /** The FQDN the TXT challenge must be published at. */
  verification_record: string;
  /** The token value. Secret to this tenant — publishing it proves control. */
  verification_token: string;
  verified_at: string | null;
  dnssec_enabled: boolean;
}

export interface RecordSet {
  name: string;
  type: string;
  ttl: number;
  records: string[];
}

export interface DynamicHost {
  id: string;
  zone_id: string;
  fqdn: string;
  ttl: number;
  last_ip: string | null;
  last_update_at: string | null;
}

export interface DynamicHostCreated extends DynamicHost {
  /** Plaintext, returned ONCE by create and rotate. The backend stores only a
   *  hash, so this value cannot be recovered — rotating is the only remedy. */
  token: string;
}

/* --- Formatting ----------------------------------------------------------
 * Sizes are shown in the units operators actually say out loud. A VM with
 * "16 GB" is how someone describes it on the phone; 17179869184 is not. */

export function bytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  if (value === 0) return "0";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / 1024 ** index;
  return `${scaled >= 10 || index === 0 ? Math.round(scaled) : scaled.toFixed(1)} ${units[index]}`;
}

export function uptime(seconds: number | null | undefined): string {
  if (!seconds) return "—";
  const days = Math.floor(seconds / 86400);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(seconds / 3600);
  if (hours >= 1) return `${hours}h`;
  return `${Math.floor(seconds / 60)}m`;
}

export function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const delta = (Date.now() - new Date(iso).getTime()) / 1000;
  if (delta < 90) return "just now";
  if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.round(delta / 3600)}h ago`;
  return `${Math.round(delta / 86400)}d ago`;
}
